import React, { FC } from 'react';
import { gqlMedia } from '@minecraft.graphql-types';
import {
  BasicContainer,
  BodyTextSmall,
  CustomBorderProps,
  FlexCol,
  FlexRow,
  H4Text,
  ImageButton,
  TextDynamic,
} from '@minecraft.atoms';
import { RadioInput } from '@minecraft.molecules';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';
import { PlanPriceSection } from '../PlanSelection/PlanPriceSection';
import { generateUniqueId } from '../helpers/functions';
import { VerticalPlanProps } from './types';
import { TemperatureValues } from '../enums/plans';
import { alacartePurchaseOption } from './alacartePurchaseOption';

interface CollapsiblePurchaseProps extends VerticalPlanProps {
  plan?: VerticalPlanProps['plan'];
  purchaseableItem?: gqlMedia[];
  children?: React.ReactNode;
  temperature?: TemperatureValues;
  isDisabled?: boolean;
  isTrialPeriodEnabled?: boolean;

  title?: string;
  icon?: string;

  billed?: string;
  currencyCode?: string;
  planTermCode?: string;
  price?: number;
  discountedPrice?: number;

  heroImage?: string;
  billingCode?: string;
  isMediaPurchase?: boolean;
}
interface CollapsibleHeaderProps extends VerticalPlanProps {
  handleChange?: () => void;
  isDisabled?: boolean;
  isSelected?: boolean;
  uniqueId?: string;
  temperature?: TemperatureValues;
  isTrialPeriodEnabled?: boolean;
  trialLengthDays?: number;
  heroImage?: string;
  billingCode?: string;
  title?: string;
  icon?: string;
  billed?: string;
  currencyCode?: string;
  planTermCode?: string;
  price?: number;
  isMediaPurchase?: boolean;
}
const CollapsibleHeader: FC<CollapsibleHeaderProps> = ({
  countryCode,
  isAnnual,
  handleChange,
  isDisabled,
  isSelected,
  uniqueId,
  temperature = TemperatureValues.vanilla,
  trialLengthDays = 0,
  isTrialPeriodEnabled = false,
  heroImage,
  billingCode,
  title,
  icon,
  billed,
  currencyCode,
  planTermCode,
  price,
  isMediaPurchase,
  plan,
}) => {
  const { t } = useTranslation();
  return (
    <FlexRow
      w="full"
      className="cn_atom_p-4"
      borderRadius="md"
      role="list"
      borderColor={`${temperature}Paywall`}
      bgColor={`${temperature}PaywallDarkBg`}
      aria-label={`${billingCode} plan options`}
      alignItems="center"
    >
      <RadioInput
        checked={isSelected}
        onChange={handleChange}
        aria-label={`Select plan with billing code ${billingCode}`}
        disabled={isDisabled}
        data-unique-id={uniqueId}
        value={billingCode}
        size="lg"
      />
      <FlexCol gap={isTrialPeriodEnabled ? '0' : '2'} ml="4">
        <FlexRow justifyContent="space-between" gap="3" alignItems="center">
          {icon && <Icon name={icon} themeColor="vanillaPaywall" />}
          <TextDynamic
            as="h1"
            fontSize="h2"
            fontWeight="semibold"
            color="default"
            mdProps={{ fontSize: 'h1', fontWeight: 'semibold' }}
          >
            {title}
          </TextDynamic>
          {plan?.discountPercent && (
            <BasicContainer bgColor="paywallBestDealBg" className="cn_shadow-md" borderRadius="md" padding={1}>
              <H4Text fontWeight="bold" color="emphasis">
                {t('common:paywall.plan.discount', { discountPercent: plan.discountPercent })}
              </H4Text>
            </BasicContainer>
          )}
          {isTrialPeriodEnabled && (
            <BasicContainer
              bgColor="paywallTrialBadge"
              borderRadius="md"
              borderPlacement="all"
              borderColor="paywallTrialBadge"
              className="cn_atom_px-2 cn_atom_py-1 cn_shadow-md"
            >
              <BodyTextSmall fontWeight="medium" color="default">
                {t('common:paywall.plan.trial.badgeCta', { days: trialLengthDays || 30 })}
              </BodyTextSmall>
            </BasicContainer>
          )}
        </FlexRow>
        <FlexCol>
          <PlanPriceSection
            countryCode={countryCode}
            isAnnual={isAnnual}
            alignItems="flex-start"
            isTrialPeriodEnabled={isTrialPeriodEnabled}
            billingCode={billingCode}
            billed={billed}
            currencyCode={currencyCode}
            planTermCode={planTermCode}
            price={price}
            strikeThroughPrice={Number(plan?.strikeThroughPrice)}
            highlightPrice={!isTrialPeriodEnabled && !plan?.discountedPrice}
            isMediaPurchase={isMediaPurchase}
          />
        </FlexCol>
      </FlexCol>
      {heroImage && <Icon name={heroImage} />}
    </FlexRow>
  );
};

const CollapsibleBody: FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const CollapsiblePurchase: FC<CollapsiblePurchaseProps> = React.memo(
  ({
    children,
    temperature,
    plan,
    selectedPlan,
    paywallLocation,
    setSelectedPlan,
    countryCode,
    isAnnual,
    isDisabled,
    isTrialPeriodEnabled,
    billed,
    currencyCode,
    planTermCode,
    price,
    title,
    icon,
    heroImage,
    billingCode,
    isMediaPurchase,
  }) => {
    const isSelected = billingCode === selectedPlan?.billingCode;
    const uniqueId = generateUniqueId({ plan, paywallLocation });
    const handleChange = () => {
      if (!isDisabled) {
        if (isMediaPurchase) {
          setSelectedPlan({ ...alacartePurchaseOption, price });
        } else {
          setSelectedPlan(plan);
        }
      }
    };

    const customBorderY = {
      borderColor: isSelected ? `${temperature}Paywall` : 'transparent',
      borderSize: 0.12,
      borderStyle: 'solid',
    } as CustomBorderProps;

    const customBorderX = {
      borderColor: isSelected ? `${temperature}Paywall` : 'transparent',
      borderSize: 0.1,
      borderStyle: 'solid',
    } as CustomBorderProps;

    return (
      <ImageButton onClick={handleChange} unsetSizing className="cn_w-full">
        <FlexCol className="cn_overflow-hidden" borderRadius="md">
          <FlexCol
            w="full"
            className={`${isSelected ? `cn_atom_p-1` : 'cn_atom_p-0'}`}
            borderRadius="md"
            grow={0}
            customBorders={{
              top: customBorderY,
              left: customBorderX,
              right: customBorderX,
              bottom: customBorderY,
            }}
          >
            <CollapsibleHeader
              plan={plan}
              temperature={temperature}
              selectedPlan={selectedPlan}
              paywallLocation={paywallLocation}
              setSelectedPlan={setSelectedPlan}
              countryCode={countryCode}
              isAnnual={isAnnual}
              isDisabled={isDisabled}
              handleChange={handleChange}
              isSelected={isSelected}
              uniqueId={uniqueId}
              trialLengthDays={plan?.trialLengthDays}
              isTrialPeriodEnabled={isTrialPeriodEnabled}
              heroImage={heroImage}
              billingCode={billingCode}
              title={title}
              icon={icon}
              billed={billed}
              currencyCode={currencyCode}
              planTermCode={planTermCode}
              price={price}
              isMediaPurchase={isMediaPurchase}
            />
            {isSelected && <CollapsibleBody>{children}</CollapsibleBody>}
          </FlexCol>
        </FlexCol>
      </ImageButton>
    );
  }
);
